import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SharePanel from "@/components/feature/SharePanel";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getNewsById, relatedNews } from "@/lib/content";
import { formatDate } from "@/utils/format";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getNewsById(id);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  const title = item ? `${item.title} - 小电影内容详情` : "资讯未找到 - 小电影";
  const description = item ? item.summary.slice(0, 120) : "该条资讯可能已下线，返回资讯分类浏览更多内容。";
  useDocumentMeta(title, description, item ? `${item.category},影视资讯,${item.title}` : "小电影,影视资讯");

  if (!item) {
    return (
      <PageShell>
        <div className="container-page py-20 text-center">
          <span className="inline-flex w-16 h-16 rounded-full bg-background-200 items-center justify-center">
            <i className="ri-newspaper-line text-2xl text-foreground-500" />
          </span>
          <h1 className="mt-5 text-xl font-extrabold text-foreground-950">没有找到这条资讯</h1>
          <p className="mt-2 text-sm text-foreground-600">链接可能已失效，去影视资讯页看看最新动态吧。</p>
          <Link
            to="/category/news"
            className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回影视资讯
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </PageShell>
    );
  }

  const related = relatedNews(item, 4);
  const readMinutes = Math.max(2, Math.round(item.content.join("").length / 300));

  return (
    <PageShell>
      <div className="bg-background-100/70 border-b border-background-200">
        <div className="container-page py-4">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "影视资讯", to: "/category/news" },
              { label: "资讯详情" },
            ]}
          />
        </div>
      </div>

      <article className="container-page py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 lg:gap-9">
          <div className="lg:col-span-2 min-w-0">
            <span className="inline-flex items-center h-6 px-2 rounded bg-accent-100 text-accent-900 text-[11px] font-medium">
              {item.category}
            </span>
            <h1 className="mt-3 text-2xl md:text-[32px] font-extrabold leading-tight tracking-tight text-foreground-950">
              {item.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-foreground-500">
              <span>{item.author}</span>
              <span className="w-1 h-1 rounded-full bg-background-300" />
              <span className="font-label">{formatDate(item.publishDate)}</span>
              <span className="w-1 h-1 rounded-full bg-background-300" />
              <span>约 {readMinutes} 分钟阅读</span>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>

            <div className="mt-5 w-full aspect-video rounded-lg overflow-hidden bg-background-200">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 小电影影视资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-6">
              <p className="text-[15px] md:text-base leading-[1.9] text-foreground-700 font-medium pl-4 border-l-2 border-primary-400">
                {item.summary}
              </p>

              <div className="mt-6 space-y-5">
                {item.content.map((paragraph, paragraphIndex) => (
                  <p
                    key={paragraphIndex}
                    className="text-[14px] md:text-[15px] leading-[1.95] text-foreground-700"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap items-center justify-between gap-3">
              <Link
                to="/category/news"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                返回影视资讯
              </Link>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-background-100 text-[13px] font-medium text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-link" />
                复制分享链接
              </button>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-[126px]">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="text-sm font-bold text-foreground-950 mb-4">相关资讯</h2>
                <ul className="divide-y divide-background-200">
                  {related.map((entry) => (
                    <li key={entry.id}>
                      <Link to={`/detail/news/${entry.id}`} className="group block py-3 cursor-pointer">
                        <p className="text-[13px] leading-6 font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {entry.title}
                        </p>
                        <p className="mt-1 text-[11px] text-foreground-500 font-label">
                          {entry.publishDate} · {entry.category}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 rounded-lg bg-background-100 border border-background-200 p-5">
                <h3 className="text-[13px] font-bold text-foreground-900">顺便看部短片？</h3>
                <p className="mt-1.5 text-[12px] leading-relaxed text-foreground-600">
                  读完资讯，用二十分钟看完一部好作品。
                </p>
                <Link
                  to="/category/shortfilms"
                  className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary-500 text-background-50 text-[12px] font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览短片
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </article>

      <SharePanel open={shareOpen} onClose={() => setShareOpen(false)} title={item.title} />
    </PageShell>
  );
}